import React, { useEffect, useState } from 'react';
import { useAuth, usePermission } from '../../core';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    getColumns,
    highResReceiptsTableHeight,
    highResTableHeight,
    receiptsTableHeight,
    SmartContractTabItems,
    SmartContractTabsType,
    tableHeight,
} from './smart-contract-helper';
import { ChainSvg } from '../../assets';
import { useReceiptList, useSmartContractList, useWalletList } from 'hooks';
import {
    BREAKPOINT_KEYS,
    ContractsSharedStyles,
    useMinHeightMediaQueries,
} from 'lib';
import { ContractsPageWrapper } from 'components/contracts-page-wrapper/ContractsPageWrapper';
import { ROLES } from 'core/contexts/permissions';
import { SMART_CONTRACT_PATHS } from 'constants/smart-contracts-paths';
import { ChainSelectorModal } from 'components';
import { GridSortModel } from '@mui/x-data-grid';

export const SmartContracts = (): React.JSX.Element => {
    const { getRoles } = useAuth();
    const isDefaultResHeightBreakpoint: boolean = useMinHeightMediaQueries(
        BREAKPOINT_KEYS.MD
    );
    const isHighResHeightBreakpoint: boolean = useMinHeightMediaQueries(
        BREAKPOINT_KEYS.LG
    );
    const isAdminRole: boolean = getRoles().includes(ROLES.admin);
    const { hasPermissions } = usePermission();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');
    const [selectedTab, setSelectedTab] =
        useState<SmartContractTabsType>('Contracts');
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
    const [rowsData, setRowsData] = useState<any>([]);
    const [selectedChain, setSelectedChain] = useState<string>('');
    const [isTabAndTableComponentVisible, setIsTabAndTableComponentVisible] =
        useState(false);
    const [addNewSmartSelectedChain, setAddNewSmartSelectedChain] =
        useState<string>('');
    const [openChainSelectorModal, setOpenChainSelectorModal] =
        useState<boolean>(false);
    const [selectChainFieldError, setSelectChainFieldError] =
        useState<boolean>(false);
    const { data: walletsData, isLoading: walletsLoading } = useWalletList();
    const columns = getColumns(
        selectedTab,
        walletsData?.data?.userWallets ?? [],
        walletsData?.data?.systemWallets ?? []
    );
    const {
        data: smartContractData,
        isLoading: smartContractDataLoading,
        refetch: refetchSmartContractData,
    } = useSmartContractList({
        chain: selectedChain,
        enabled: !!selectedChain,
    });
    const {
        data: receiptListData,
        isLoading: receiptListDataLoading,
        refetch: refetchReceiptListData,
    } = useReceiptList({
        chain: selectedChain,
        contractType: 'smartcontracts',
        receiptType: isAdminRole ? 'admin' : 'user',
        enabled: !!selectedChain,
    });

    useEffect(() => {
        if (location?.state) {
            setSelectedChain(location?.state);
        }
        if (tab) {
            setSelectedTab(tab as SmartContractTabsType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedChain) {
            setIsLoading(true);
            setIsTabAndTableComponentVisible(true);
            setRowsData([]);
            setIsDataLoading(true);
        }
        if (
            selectedTab === 'Contracts' &&
            selectedChain &&
            !smartContractDataLoading
        ) {
            const rows = smartContractData?.data?.map((item) => ({
                id: item.header?.id,
                description: item.description,
                name: item.name,
                timestamp: item.header?.timestamp,
            }));
            setRowsData(rows);
            setIsDataLoading(false);
        } else if (
            selectedTab === 'Receipts' &&
            selectedChain &&
            !receiptListDataLoading &&
            !walletsLoading
        ) {
            const rows = receiptListData?.data?.map((item) => ({
                id: item.contractSdoId,
                timestamp: item.header.timestamp,
                contractOwnerId: item.contractOwnerId,
                effectiveId: item.effectiveId,
                loggedInId: item.loggedInId,
                function: item.receiptSmartContract.function,
                ...item,
            }));
            setRowsData(rows);
            setIsDataLoading(false);
        }
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        smartContractDataLoading,
        selectedChain,
        selectedTab,
        receiptListDataLoading,
        walletsLoading,
    ]);

    const onTabChange = (event: React.SyntheticEvent, newValue: any): void => {
        if (event && selectedChain) {
            setSelectedTab(newValue);
            setRowsData([]);
            setIsLoading(true);
            if (newValue === 'Contracts') {
                refetchSmartContractData();
            } else if (newValue === 'Receipts') {
                refetchReceiptListData();
            } else {
                setRowsData([]);
            }
        }
    };

    const handleRowClick = (row: any): void => {
        if (!selectedChain) return;
        if (selectedTab === 'Contracts') {
            navigate(
                `${SMART_CONTRACT_PATHS.LANDING_PAGE}/${selectedChain}/${row.id}`
            );
        } else {
            const { id, timestamp, ...receiptData } = row.row;
            navigate(
                `${SMART_CONTRACT_PATHS.LANDING_PAGE}/receipt/${selectedChain}/${row.id}`,
                {
                    state: receiptData,
                }
            );
        }
    };

    const handleAddSmartContract = (): void => {
        const permission = hasPermissions('createData');
        if (permission) {
            setOpenChainSelectorModal(true);
            if (selectedChain) {
                setAddNewSmartSelectedChain(selectedChain);
            }
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handleCloseChainSelectorModal = (): void => {
        setOpenChainSelectorModal(false);
        setSelectChainFieldError(false);
        setAddNewSmartSelectedChain('');
    };

    const handleNextButtonClick = (): void => {
        if (!addNewSmartSelectedChain) {
            setSelectChainFieldError(true);
        } else {
            setOpenChainSelectorModal(false);
            setSelectChainFieldError(false);
            navigate(
                `${SMART_CONTRACT_PATHS.CREATE_NEW_SMART_CONTRACT}?chain=${addNewSmartSelectedChain}`
            );
        }
    };

    const getSortModel = (): GridSortModel => {
        if (selectedTab === 'Contracts') {
            return [{ field: 'name', sort: 'asc' }];
        } else {
            return [{ field: 'timestamp', sort: 'desc' }];
        }
    };

    const isSelectedTabContracts = (): boolean => {
        return selectedTab === 'Contracts';
    };

    const getDynamicTableHeightForContractsTable = (): number => {
        if (isHighResHeightBreakpoint || isDefaultResHeightBreakpoint) {
            return highResTableHeight;
        } else {
            return tableHeight;
        }
    };

    const getDynamicTableHeightForReceiptsTable = (): number => {
        if (isHighResHeightBreakpoint || isDefaultResHeightBreakpoint) {
            return highResReceiptsTableHeight;
        } else {
            return receiptsTableHeight;
        }
    };

    const getDynamicTableHeight = (): number => {
        if (isSelectedTabContracts()) {
            return getDynamicTableHeightForContractsTable();
        } else {
            return getDynamicTableHeightForReceiptsTable();
        }
    };

    return (
        <>
            <ContractsPageWrapper
                title={'Smart Contracts'}
                handleAddContract={handleAddSmartContract}
                rowsData={rowsData}
                isLoading={
                    isDataLoading ||
                    isLoading ||
                    smartContractDataLoading ||
                    receiptListDataLoading
                }
                columns={columns}
                handleRowClick={handleRowClick}
                emptyStateIcon={ChainSvg}
                emptyStateBodyMessage={
                    selectedTab === 'Contracts'
                        ? 'Add a new smart contract using the button at the top right of the screen.'
                        : 'Receipts will populate when when you create and execute a smart contract.'
                }
                tabItems={SmartContractTabItems}
                onTabChange={onTabChange}
                selectedTab={selectedTab}
                tabsStyles={ContractsSharedStyles.tabsStyles}
                tabsContainerStyles={ContractsSharedStyles.tabsContainerStyles}
                tabItemStyles={ContractsSharedStyles.tabItemStyles}
                tableHeight={getDynamicTableHeight()}
                selectedChain={selectedChain}
                setSelectedChain={setSelectedChain}
                displayPermissionDialog={displayPermissionDialog}
                setDisplayPermissionDialog={setDisplayPermissionDialog}
                isTabAndTableComponentVisible={isTabAndTableComponentVisible}
                sortModel={getSortModel()}
            />
            {openChainSelectorModal && (
                <ChainSelectorModal
                    chainSelectorModal={openChainSelectorModal}
                    chainSelectorModalTitle='New Smart Contract'
                    chainSelectorModalSubheading={
                        'Select a chain to place your smart contract.'
                    }
                    handleCancelButtonClick={handleCloseChainSelectorModal}
                    handleCloseModal={handleCloseChainSelectorModal}
                    handleNextButtonClick={handleNextButtonClick}
                    selectedChain={addNewSmartSelectedChain}
                    setSelectedChain={setAddNewSmartSelectedChain}
                    selectChainFieldError={selectChainFieldError}
                    setSelectChainFieldError={setSelectChainFieldError}
                />
            )}
        </>
    );
};
